import React from "react";
import { ArrowRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function HeroSection({ onStartTrial, onVerifyFree }) {
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "VALIDMAIL",
            applicationCategory: "BusinessApplication",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              ratingCount: "2847",
            },
            operatingSystem: "Web Browser",
            description: "Real-time email validation and verification platform for teams",
          })}
        </script>
      </Helmet>

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50/50 via-white to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl" />
          <div className="absolute top-60 -left-40 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Content (Headline) - ensure comes first on mobile */}
            <div className="text-center lg:text-left order-1">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 leading-tight mb-6">
                Send email with confidence.{" "}
                <span className="text-sky-600 dark:text-sky-400">
                  Every address, verified.
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto lg:mx-0">
                VALIDMAIL checks every email in milliseconds so your campaigns hit
                inboxes, not bounce lists. Built for teams who care about
                deliverability, speed, and privacy.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <button
                  onClick={onStartTrial}
                  className="px-8 py-4 rounded-full text-base font-semibold bg-sky-600 text-white hover:bg-sky-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200"
                  aria-label="Start free trial"
                >
                  Start free trial
                </button>

                <button
                  onClick={onVerifyFree}
                  className="px-8 py-4 rounded-full text-base font-semibold border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all inline-flex items-center gap-2 group"
                  aria-label="Verify 100 emails free"
                >
                  Verify 100 emails free
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 justify-center lg:justify-start">
                  <Check className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700 dark:text-slate-300 text-left">
                    Real-time validation for forms, CRMs, and apps
                  </p>
                </div>
                <div className="flex items-start gap-3 justify-center lg:justify-start">
                  <Check className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700 dark:text-slate-300 text-left">
                    Bulk list cleaning with clear, actionable results
                  </p>
                </div>
                <div className="flex items-start gap-3 justify-center lg:justify-start">
                  <Check className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700 dark:text-slate-300 text-left">
                    Global compliance incl. GDPR, CCPA, and India DPDP
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-500 dark:text-slate-400">
                No code required to start. Upgrade only when you're ready.
              </p>
            </div>

            {/* Right Column - Product Mockup (comes second on mobile) */}
            <div className="relative order-2 lg:order-last">
              <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="w-full">
                      <input
                        type="text"
                        placeholder="user@example.com"
                        className="w-full bg-transparent border-none outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
                        disabled
                        aria-label="example email"
                      />
                    </div>
                    <button
                      className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium"
                      aria-hidden
                    >
                      Verify
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-900 dark:text-green-100">Valid</p>
                        <p className="text-xs text-green-700 dark:text-green-300">Safe to send</p>
                      </div>
                      <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      <div className="w-2 h-2 bg-red-500 rounded-full" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-900 dark:text-red-100">Invalid</p>
                        <p className="text-xs text-red-700 dark:text-red-300">Mailbox not found</p>
                      </div>
                      <span className="text-red-600 dark:text-red-400 font-bold">×</span>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <div className="w-2 h-2 bg-amber-500 rounded-full" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Risky</p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">Disposable email detected</p>
                      </div>
                      <span className="text-amber-600 dark:text-amber-400 font-bold">!</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">&lt;300ms</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Response time</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">98%+</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Accuracy</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">24/7</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Uptime</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -z-10 -top-4 -right-4 w-72 h-72 bg-sky-200/30 dark:bg-sky-900/30 rounded-2xl blur-2xl" />
              <div className="absolute -z-10 -bottom-4 -left-4 w-72 h-72 bg-blue-200/30 dark:bg-blue-900/30 rounded-2xl blur-2xl" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
